import React, { useEffect, useState } from 'react';
import { Check, ArrowRight, Award } from 'lucide-react';
import { CHECKOUT_URL, CHECKOUT_URL_PLUS } from '../config';
const baseInclusions = ['12 Tomos Clínicos', '6 Bonos Operativos', '80+ Plantillas y Fichas', '40+ Recetas con info nutricional completa', '8 Listas de compras por objetivo clínico', '30+ Scripts de WhatsApp listos para usar', 'Calculador de Macros en Excel', 'Masterclass Consultorio Virtual'];
const specializations = [{
  emoji: '🧬',
  title: 'Endocrino-Metabolia Pro',
  desc: 'Para el paciente que hace todo bien y no baja de peso. Interpretá los ejes hormonales (cortisol, DHEA, T3 libre, T3 reversa, leptina, insulina post-carga) y actuá con suplementación ortomolecular por perfil.'
}, {
  emoji: '🛡️',
  title: 'Inmunonutrición & Autoinmunidad',
  desc: 'Para Hashimoto, artritis, psoriasis o Crohn. Protocolo AIP completo en 3 fases, evaluación de permeabilidad intestinal y micronutrientes inmunomoduladores con dosis y formas biodisponibles.'
}, {
  emoji: '🧠',
  title: 'Neuro-Nutrición & Circuito de Recompensa',
  desc: 'Para el paciente que sabe qué hacer pero no lo sostiene. Identificá el perfil neuroquímico (dopamina, serotonina, GABA), aplicá el reset dopaminérgico y usá precursores de neurotransmisores con criterio clínico.'
}];
type PlanId = 'base' | 'plus';
const plans = {
  base: {
    name: 'EL ARSENAL DEL NUTRI',
    summary: '12 tomos clínicos + 6 bonos',
    oldPrice: '$335.000+',
    price: '$19.990',
    checkout: CHECKOUT_URL,
    cta: 'QUIERO EL ARSENAL AHORA'
  },
  plus: {
    name: 'ARSENAL + 3 ESPECIALIZACIONES',
    summary: 'Todo el Arsenal + 3 nichos de alta rentabilidad',
    oldPrice: '$500.000+',
    price: '$29.990',
    checkout: CHECKOUT_URL_PLUS,
    cta: 'QUIERO EL ARSENAL + ESPECIALIZACIONES'
  }
} as const;
interface TicketProps {
  id: PlanId;
  selected: boolean;
  onSelect: (id: PlanId) => void;
  badge?: string;
}
function Ticket({
  id,
  selected,
  onSelect,
  badge
}: TicketProps) {
  const plan = plans[id];
  return <button type="button" role="radio" aria-checked={selected} onClick={() => onSelect(id)} className={`w-full text-left rounded-2xl border-2 p-5 transition-all flex items-center gap-4 ${selected ? 'border-brand-teal bg-brand-teal/5 shadow-md' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-116">
      <span className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected ? 'border-brand-teal' : 'border-gray-300'}`} aria-hidden="true" data-id="element-117">
        {selected && <span className="w-2.5 h-2.5 rounded-full bg-brand-teal" data-id="element-118" />}
      </span>

      <span className="flex-grow min-w-0" data-id="element-119">
        <span className="flex flex-wrap items-center gap-2" data-id="element-120">
          <span className="font-bold text-brand-charcoal text-sm sm:text-base uppercase tracking-wide" data-id="element-121">
            {plan.name}
          </span>
          {badge && <span className="inline-flex items-center gap-1 bg-amber-400 text-amber-950 text-[11px] font-bold px-2 py-0.5 rounded-full" data-id="element-122">
              <Award className="w-3 h-3" data-id="element-123" />
              {badge}
            </span>}
        </span>
        <span className="block text-xs sm:text-sm text-gray-500 mt-0.5" data-id="element-124">
          {plan.summary}
        </span>
      </span>

      <span className="text-right flex-shrink-0" data-id="element-125">
        <span className="block text-xs text-gray-400 line-through" data-id="element-126">
          {plan.oldPrice}
        </span>
        <span className={`block text-xl sm:text-2xl font-bold ${selected ? 'text-brand-teal' : 'text-brand-charcoal'}`} data-id="element-127">
          {plan.price}
        </span>
      </span>
    </button>;
}
export function OfferSection() {
  const [selected, setSelected] = useState<PlanId>('plus');
  const plan = plans[selected];
  useEffect(() => {
    const handler = () => setSelected('plus');
    window.addEventListener('select-plus-plan', handler);
    return () => window.removeEventListener('select-plus-plan', handler);
  }, []);
  return <section id="comprar" className="py-24 bg-white scroll-mt-10" data-id="element-128">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-129">
        {/* Selector header */}
        <div className="text-center mb-8" data-id="element-130">
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-brand-charcoal mb-2" data-id="element-131">
            Elegí cómo querés llevarlo
          </h2>
          <p className="text-brand-teal font-bold uppercase tracking-widest text-sm" data-id="element-132">
            🏅 Pago único — Elegí tu opción
          </p>
        </div>

        {/* Ticket selector */}
        <div className="space-y-4 mb-10" role="radiogroup" aria-label="Elegí tu plan" data-id="element-133">
          <Ticket id="base" selected={selected === 'base'} onSelect={setSelected} data-id="element-134" />
          <Ticket id="plus" selected={selected === 'plus'} onSelect={setSelected} badge="MÁS COMPLETO" data-id="element-135" />
        </div>

        {/* Detail card for selected plan */}
        <div className="bg-white rounded-3xl shadow-2xl border-4 border-brand-teal overflow-hidden" data-id="element-136">
          <div className="bg-brand-teal text-white text-center py-8 px-6" data-id="element-137">
            <h3 className="text-2xl sm:text-3xl font-serif font-bold mb-2" data-id="element-138">
              {plan.name}
            </h3>
            <p className="text-brand-tealLight font-bold text-lg tracking-widest uppercase" data-id="element-139">
              Sistema Operativo 360°
            </p>
          </div>

          <div className="p-8 sm:p-12" data-id="element-140">
            {selected === 'base' ? <ul className="space-y-4 mb-10" data-id="element-141">
                {baseInclusions.map((item, i) => <li key={i} className="flex items-center gap-3 text-lg text-gray-700 font-medium" data-id="element-142">
                    <div className="bg-brand-accent/10 p-1 rounded-full" data-id="element-143">
                      <Check className="w-5 h-5 text-brand-accent" data-id="element-144" />
                    </div>
                    {item}
                  </li>)}
              </ul> : <div className="mb-10" data-id="element-145">
                {/* Bloque 1: TODO el Arsenal del Nutri */}
                <div className="bg-brand-teal/5 border border-brand-teal/15 rounded-2xl p-5 sm:p-6 mb-6" data-id="element-146">
                  <p className="flex items-center gap-2 font-bold text-brand-teal text-lg mb-3" data-id="element-147">
                    <Check className="w-5 h-5 text-brand-accent flex-shrink-0" data-id="element-148" />
                    Te llevás el Arsenal del Nutri COMPLETO
                  </p>
                  <p className="text-sm text-gray-600 mb-4" data-id="element-149">
                    Los 12 tomos clínicos, los 6 bonos y todas las herramientas
                    de la opción base. Nada queda afuera:
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2" data-id="element-150">
                    {baseInclusions.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-151">
                        <Check className="w-4 h-4 text-brand-accent flex-shrink-0 mt-0.5" data-id="element-152" />
                        {item}
                      </li>)}
                  </ul>
                </div>

                {/* Bloque 2: y ADEMÁS las 3 especializaciones */}
                <div className="flex items-center gap-3 mb-5" data-id="element-153">
                  <span className="text-2xl" data-id="element-154">➕</span>
                  <p className="font-bold text-brand-charcoal text-lg uppercase tracking-wide" data-id="element-155">
                    Y además, las 3 especializaciones de alta rentabilidad
                  </p>
                </div>
                <div className="space-y-5" data-id="element-156">
                  {specializations.map((spec, i) => <div key={i} className="flex items-start gap-3" data-id="element-157">
                      <div className="bg-brand-accent/10 p-1 rounded-full mt-1 flex-shrink-0" data-id="element-158">
                        <Check className="w-5 h-5 text-brand-accent" data-id="element-159" />
                      </div>
                      <div data-id="element-160">
                        <p className="font-bold text-brand-charcoal" data-id="element-161">
                          {spec.emoji} {spec.title}
                        </p>
                        <p className="text-sm text-gray-600 leading-relaxed mt-1" data-id="element-162">
                          {spec.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>}

            <div className="border-t border-gray-200 pt-8 text-center" data-id="element-163">
              <p className="text-gray-400 font-bold text-lg mb-2 line-through" data-id="element-164">
                VALOR REAL: {plan.oldPrice} ARS
              </p>

              <div className="mb-8" data-id="element-165">
                <p className="text-brand-charcoal font-bold text-xl mb-1" data-id="element-166">
                  HOY:
                </p>
                <p className="text-5xl sm:text-6xl font-bold text-brand-teal mb-3" data-id="element-167">
                  {plan.price} ARS
                </p>
                <p className="text-gray-500 font-medium uppercase tracking-wide text-sm" data-id="element-168">
                  PAGO ÚNICO · ACCESO INMEDIATO
                </p>
              </div>

              <a href={plan.checkout} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-center gap-2 bg-brand-accent hover:bg-brand-accentHover text-white font-bold text-xl py-5 px-8 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" data-id="element-169">
                {plan.cta}
                <ArrowRight className="w-5 h-5" data-id="element-170" />
              </a>

              {selected === 'plus' && <p className="text-sm text-gray-400 italic mt-4" data-id="element-171">
                  Podés sumar las especializaciones cuando quieras.
                </p>}
            </div>
          </div>
        </div>
      </div>
    </section>;
}